(() => {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activeOverlay = null;
  let returnFocus = null;
  let activeMegaItem = null;
  let megaCloseTimer = null;

  const cancelMegaClose = () => {
    window.clearTimeout(megaCloseTimer);
    megaCloseTimer = null;
  };

  const closeMegaMenu = (item = activeMegaItem, restoreFocus = false) => {
    if (!item) return;
    cancelMegaClose();
    const trigger = item.querySelector('[data-mega-trigger]');
    const menu = item.querySelector('[data-mega-menu]');
    item.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    menu?.classList.remove('is-open');
    menu?.setAttribute('aria-hidden', 'true');
    if (restoreFocus) trigger?.focus();
    if (activeMegaItem === item) activeMegaItem = null;
  };

  const openMegaMenu = (item) => {
    if (!item) return;
    cancelMegaClose();
    if (activeMegaItem && activeMegaItem !== item) closeMegaMenu(activeMegaItem);
    const trigger = item.querySelector('[data-mega-trigger]');
    const menu = item.querySelector('[data-mega-menu]');
    item.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    menu?.classList.add('is-open');
    menu?.setAttribute('aria-hidden', 'false');
    activeMegaItem = item;
  };

  const scheduleMegaClose = (item) => {
    cancelMegaClose();
    megaCloseTimer = window.setTimeout(() => closeMegaMenu(item), 320);
  };

  const announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  const trapFocus = (event) => {
    if (!activeOverlay || event.key !== 'Tab') return;
    const focusable = [...activeOverlay.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const closeOverlay = (overlay = activeOverlay, restore = true) => {
    if (!overlay) return;
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    document.querySelectorAll(`[aria-controls="${overlay.id}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (activeOverlay === overlay) activeOverlay = null;
    if (restore && returnFocus instanceof HTMLElement) returnFocus.focus();
    returnFocus = null;
  };

  const openOverlay = (id, opener) => {
    const overlay = document.getElementById(id);
    if (!overlay) return;
    closeMegaMenu();
    const previousReturnFocus = returnFocus;
    const openerInsideActiveOverlay = activeOverlay && opener && activeOverlay.contains(opener);
    if (activeOverlay && activeOverlay !== overlay) closeOverlay(activeOverlay, false);
    returnFocus = openerInsideActiveOverlay ? previousReturnFocus : (opener || document.activeElement);
    overlay.hidden = false;
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    document.querySelectorAll(`[aria-controls="${id}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    activeOverlay = overlay;
    window.requestAnimationFrame(() => {
      const target = overlay.querySelector('[autofocus], input[type="search"], [role="dialog"]');
      if (target) target.focus();
    });
  };

  document.querySelectorAll('[data-mega-item]').forEach((item) => {
    const trigger = item.querySelector('[data-mega-trigger]');
    const menu = item.querySelector('[data-mega-menu]');
    item.addEventListener('pointerenter', () => openMegaMenu(item));
    item.addEventListener('pointerleave', () => scheduleMegaClose(item));
    item.addEventListener('focusin', () => {
      cancelMegaClose();
      if (menu?.contains(document.activeElement)) openMegaMenu(item);
    });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) scheduleMegaClose(item);
    });
    trigger?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openMegaMenu(item);
        const links = [...menu.querySelectorAll('a[href]')];
        links[event.key === 'ArrowDown' ? 0 : links.length - 1]?.focus();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeMegaMenu(item, true);
      }
    });
    menu?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMegaMenu(item, true);
      }
    });
  });

  const replaceCartDrawer = (html, shouldOpen = true) => {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = documentFragment.querySelector('#CartDrawer');
    const currentDrawer = document.querySelector('#CartDrawer');
    if (!nextDrawer || !currentDrawer) return;
    currentDrawer.replaceWith(nextDrawer);
    if (shouldOpen) openOverlay('CartDrawer', returnFocus);
  };

  const refreshCart = async (shouldOpen = true) => {
    const [drawerResponse, cartResponse] = await Promise.all([
      fetch(window.theme.routes.cart, { headers: { Accept: 'text/html' } }),
      fetch(`${window.theme.routes.cart}.js`, { headers: { Accept: 'application/json' } })
    ]);
    if (!drawerResponse.ok || !cartResponse.ok) throw new Error(window.theme.strings.cartError);
    const drawerHtml = await drawerResponse.text();
    const cart = await cartResponse.json();
    replaceCartDrawer(drawerHtml, shouldOpen);
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
      const link = count.closest('[aria-label]');
      if (link) link.setAttribute('aria-label', window.theme.strings.cartLabel.replace('__COUNT__', cart.item_count));
    });
    return cart;
  };

  const addToCart = async (form) => {
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-form-status]');
    if (submit) submit.setAttribute('aria-busy', 'true');
    if (status) { status.hidden = true; status.textContent = ''; }
    try {
      const response = await fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || window.theme.strings.cartError);
      if (window.theme.settings.cartType === 'drawer') await refreshCart(true);
      else window.location.assign(window.theme.routes.cart);
      announce(result.title || window.theme.strings.addToCart);
    } catch (error) {
      if (status) { status.textContent = error.message; status.hidden = false; }
      announce(error.message);
    } finally {
      if (submit) submit.removeAttribute('aria-busy');
    }
  };

  const changeCartLine = async (line, quantity) => {
    try {
      const response = await fetch(window.theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || window.theme.strings.cartError);
      await refreshCart(true);
      announce(window.theme.strings.cartUpdated);
    } catch (error) { announce(error.message); }
  };

  document.addEventListener('click', (event) => {
    const megaTrigger = event.target.closest('[data-mega-trigger]');
    if (megaTrigger) {
      event.preventDefault();
      const item = megaTrigger.closest('[data-mega-item]');
      if (item === activeMegaItem) closeMegaMenu(item);
      else openMegaMenu(item);
      return;
    }

    const mobileNavToggle = event.target.closest('[data-mobile-nav-toggle]');
    if (mobileNavToggle) {
      const category = mobileNavToggle.closest('[data-mobile-nav-category]');
      const submenu = document.getElementById(mobileNavToggle.getAttribute('aria-controls'));
      const expanded = mobileNavToggle.getAttribute('aria-expanded') === 'true';
      mobileNavToggle.setAttribute('aria-expanded', String(!expanded));
      category?.classList.toggle('is-open', !expanded);
      if (submenu) submenu.hidden = expanded;
      return;
    }

    const openTrigger = event.target.closest('[data-open-drawer]');
    if (openTrigger) { event.preventDefault(); openOverlay(openTrigger.dataset.openDrawer, openTrigger); return; }

    const closeTrigger = event.target.closest('[data-close]');
    if (closeTrigger) { event.preventDefault(); closeOverlay(closeTrigger.closest('[data-drawer], [data-modal]')); return; }

    const quantityButton = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (quantityButton) {
      const input = quantityButton.closest('[data-quantity], .quantity').querySelector('[data-quantity-input]');
      const step = Number(input.step || 1);
      const next = quantityButton.hasAttribute('data-quantity-plus') ? Number(input.value) + step : Number(input.value) - step;
      input.value = Math.min(Number(input.max || Infinity), Math.max(Number(input.min || 0), next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) { changeCartLine(Number(removeButton.closest('[data-line]').dataset.line), 0); return; }

    const lightboxButton = event.target.closest('[data-lightbox-image]');
    if (lightboxButton) {
      const modal = document.getElementById('MediaLightbox');
      const content = modal?.querySelector('[data-modal-content]');
      if (content) { const image = new Image(); image.src = lightboxButton.dataset.lightboxImage; image.alt = lightboxButton.querySelector('img')?.alt || ''; content.replaceChildren(image); openOverlay('MediaLightbox', lightboxButton); }
      return;
    }

    const shareButton = event.target.closest('[data-share-url]');
    if (shareButton) {
      if (navigator.share) navigator.share({ title: document.title, url: shareButton.dataset.shareUrl }).catch(() => {});
      else navigator.clipboard?.writeText(shareButton.dataset.shareUrl).then(() => announce(window.theme.strings.linkCopied));
      return;
    }

    const dismiss = event.target.closest('[data-dismiss-announcement]');
    if (dismiss) {
      const announcement = dismiss.closest('[data-announcement]');
      announcement?.remove();
      try { sessionStorage.setItem(`announcement-${dismiss.dataset.dismissAnnouncement}`, 'dismissed'); } catch (_error) { /* Storage may be unavailable. */ }
    }

    if (activeMegaItem && !event.target.closest('[data-mega-item]')) closeMegaMenu();
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    addToCart(form);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('.cart-drawer [data-quantity-input]');
    if (!input) return;
    const item = input.closest('[data-line]');
    if (item) changeCartLine(Number(item.dataset.line), Number(input.value));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeOverlay) closeOverlay(activeOverlay);
    else if (event.key === 'Escape' && activeMegaItem) closeMegaMenu(activeMegaItem, true);
    trapFocus(event);
  });

  document.querySelectorAll('[data-announcement]').forEach((announcement) => {
    try { if (sessionStorage.getItem(`announcement-${announcement.querySelector('[data-dismiss-announcement]')?.dataset.dismissAnnouncement}`)) announcement.remove(); } catch (_error) { /* Storage may be unavailable. */ }
  });

  document.querySelectorAll('[data-predictive-search]').forEach((form) => {
    if (!window.theme.settings.enablePredictiveSearch && window.theme.settings.enablePredictiveSearch !== undefined) return;
    const input = form.querySelector('[data-predictive-input]');
    const results = form.querySelector('[data-predictive-results]');
    const clear = form.querySelector('[data-search-clear]');
    let timeout;
    input.addEventListener('input', () => {
      window.clearTimeout(timeout);
      const query = input.value.trim();
      clear.hidden = !query;
      if (query.length < 2) { results.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
      timeout = window.setTimeout(async () => {
        results.setAttribute('aria-busy', 'true');
        try {
          const url = `${window.theme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,query,page,article&resources[limit]=6&section_id=predictive-search`;
          const response = await fetch(url);
          if (!response.ok) throw new Error(window.theme.strings.searchError);
          const markup = await response.text();
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          results.innerHTML = parsed.querySelector('#shopify-section-predictive-search')?.innerHTML || markup;
          input.setAttribute('aria-expanded', 'true');
        } catch (error) { results.innerHTML = `<p class="form-status form-status--error">${error.message}</p>`; }
        finally { results.removeAttribute('aria-busy'); }
      }, 250);
    });
    form.addEventListener('reset', () => { window.setTimeout(() => { results.innerHTML = ''; clear.hidden = true; input.setAttribute('aria-expanded', 'false'); input.focus(); }, 0); });
  });

  document.querySelectorAll('product-recommendations').forEach(async (element) => {
    if (!element.dataset.url) return;
    try {
      const response = await fetch(element.dataset.url);
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const content = parsed.querySelector('product-recommendations');
      if (content?.innerHTML.trim()) element.innerHTML = content.innerHTML;
    } catch (_error) { element.remove(); }
  });
})();
